
import { useEffect, useState, useCallback } from "react";
import { Loader2, Users, CheckCircle, Trash2 } from "lucide-react";
import { Participante } from "@/types/participante";
import { listarEspera, removerEspera, confirmarDaEspera } from "@/lib/participantes";
import { formatarCPF, formatarTelefone } from "@/lib/masks";

interface Props {
  edicaoId: string;
  onConfirmado: () => void;
}

export default function ListaEsperaTab({ edicaoId, onConfirmado }: Props) {
  const [lista, setLista] = useState<Participante[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [confirmando, setConfirmando] = useState<string | null>(null);
  const [removendo, setRemovendoId] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      setLista(await listarEspera(edicaoId));
    } finally {
      setCarregando(false);
    }
  }, [edicaoId]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const handleConfirmar = async (p: Participante) => {
    const ok = window.confirm(`Confirmar "${p.nomeCompleto}" como participante?`);
    if (!ok) return;
    setConfirmando(p.id);
    try {
      await confirmarDaEspera(edicaoId, p);
      await carregar();
      onConfirmado();
    } finally {
      setConfirmando(null);
    }
  };

  const handleRemover = async (p: Participante) => {
    const ok = window.confirm(`Remover "${p.nomeCompleto}" da lista de espera?`);
    if (!ok) return;
    setRemovendoId(p.id);
    try {
      await removerEspera(edicaoId, p.id);
      await carregar();
    } finally {
      setRemovendoId(null);
    }
  };

  if (carregando) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-5 h-5 animate-spin text-black/20" />
      </div>
    );
  }

  if (lista.length === 0) {
    return (
      <div className="bg-white rounded-2xl border border-black/5 p-16 flex flex-col items-center justify-center text-center gap-3">
        <div className="w-12 h-12 rounded-xl bg-black/5 flex items-center justify-center">
          <Users className="w-5 h-5 text-black/20" />
        </div>
        <p className="text-sm font-medium text-black/50">
          Nenhuma pessoa na lista de espera.
        </p>
        <p className="text-xs text-black/30">
          Os inscritos aparecerão aqui quando o formulário da landing page for preenchido.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-sm text-black/40">
        {lista.length} pessoa{lista.length !== 1 ? "s" : ""} na lista de espera
      </p>

      <div className="bg-white rounded-2xl border border-black/5 overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-black/5">
              <th className="text-left px-5 py-3.5 text-xs font-medium text-black/40 uppercase tracking-wider">
                #
              </th>
              <th className="text-left px-5 py-3.5 text-xs font-medium text-black/40 uppercase tracking-wider">
                Nome
              </th>
              <th className="text-left px-5 py-3.5 text-xs font-medium text-black/40 uppercase tracking-wider">
                CPF
              </th>
              <th className="text-left px-5 py-3.5 text-xs font-medium text-black/40 uppercase tracking-wider">
                E-mail
              </th>
              <th className="text-left px-5 py-3.5 text-xs font-medium text-black/40 uppercase tracking-wider">
                WhatsApp
              </th>
              <th className="px-5 py-3.5" />
            </tr>
          </thead>
          <tbody className="divide-y divide-black/5">
            {lista.map((p, i) => (
              <tr key={p.id} className="hover:bg-black/[0.02] transition group">
                <td className="px-5 py-4 text-black/30 text-xs">{i + 1}</td>
                <td className="px-5 py-4 font-medium text-black">{p.nomeCompleto}</td>
                <td className="px-5 py-4 text-black/50 font-mono text-xs">
                  {formatarCPF(p.cpf)}
                </td>
                <td className="px-5 py-4 text-black/50 text-xs">
                  {p.email || "-"}
                </td>
                <td className="px-5 py-4 text-black/50 text-xs">
                  {formatarTelefone(p.whatsapp)}
                </td>
                <td className="px-5 py-4">
                  <div className="flex items-center gap-1 justify-end opacity-0 group-hover:opacity-100 transition">
                    <button
                      onClick={() => handleConfirmar(p)}
                      disabled={confirmando === p.id}
                      title="Confirmar participante"
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-green-700 bg-green-50 hover:bg-green-100 transition disabled:opacity-50"
                    >
                      {confirmando === p.id ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <CheckCircle className="w-3.5 h-3.5" />
                      )}
                      Confirmar
                    </button>
                    <button
                      onClick={() => handleRemover(p)}
                      disabled={removendo === p.id}
                      title="Remover da lista"
                      className="w-8 h-8 flex items-center justify-center rounded-lg text-black/30 hover:text-red-500 hover:bg-red-50 transition disabled:opacity-50"
                    >
                      {removendo === p.id ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <Trash2 className="w-3.5 h-3.5" />
                      )}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
